import {
  TPR_BILTMORE_AHI_DEPARTMENT_ID,
  TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
  TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID,
  TPR_BILTMORE_CENTRAL_DEPARTMENT_ID,
  TPR_BILTMORE_DEPARTMENT_ID,
  TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
  TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
  TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
  TPR_BILTMORE_VERRADO_DEPARTMENT_ID,
  TPR_BILTMORE_WEST_DEPARTMENT_ID,
} from "@/configuration/groups/tpr/athena";
import {
  getTprSupervisingPhysicianNpi,
  TprBiltmoreAhiProviderNpi,
  TprBiltmoreArrowheadProviderNpi,
  TprBiltmoreCamelbackProviderNpi,
  TprBiltmoreCasaGrandeProviderNpi,
  TprBiltmoreGoodyearProviderNpi,
  TprBiltmorePeoriaProviderNpi,
  TprBiltmoreScottsdaleProviderNpi,
  TprBiltmoreWestProviderNpi,
} from "@/configuration/groups/tpr/configuration";
import { getClauseBeforeMatch, textMatchesUnnegated } from "@/src/lib/utils/negated-keyword-match";
import type { DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { normalizeICD10Code } from "@/src/models/icd10";

/**
 * TPR's four cardiology sub-specialty categories, used by the receiving-provider resolver's
 * "no receiving provider on the referral, pick one who treats this diagnosis" branch (see
 * receiving-provider-location.ts). Distinct from the reviewer checklist's bucket-assignment logic
 * (sync-checklist.ts), which deliberately does NOT gate on specialty/diagnosis per ops request
 * (see the "drop specialty gate" commit) — that removal was scoped to bucket assignment only, not
 * to provider *selection* when the referral doesn't name one.
 */
export enum TprSpecialtyCategory {
  GENERAL = "general",
  INTERVENTIONAL = "interventional",
  STRUCTURAL = "structural",
  ELECTROPHYSIOLOGY = "electrophysiology",
}

type DiagnosisSpecialtyPattern = {
  label: string;
  pattern: RegExp;
  categories: TprSpecialtyCategory[];
  pickOnly?: true;
  namedProviderAlsoFits?: TprSpecialtyCategory;
  /** Chronic conditions and implanted devices persist, so "history of afib" still counts as current. */
  persistsWhenHistorical?: true;
};

const { GENERAL, INTERVENTIONAL, STRUCTURAL, ELECTROPHYSIOLOGY } = TprSpecialtyCategory;

/** Ops-facing labels for the four categories — TPR ops says "EP", not "electrophysiology". */
const TPR_SPECIALTY_CATEGORY_LABELS: Readonly<Record<TprSpecialtyCategory, string>> = {
  [GENERAL]: "General",
  [INTERVENTIONAL]: "Interventional",
  [STRUCTURAL]: "Structural",
  [ELECTROPHYSIOLOGY]: "EP",
};

/** Comma-joined ops labels for the reviewer UI, e.g. "Interventional, EP". Empty in, empty out. */
export function describeTprSpecialtyCategories(categories: readonly TprSpecialtyCategory[]): string {
  return categories.map((category) => TPR_SPECIALTY_CATEGORY_LABELS[category]).join(", ");
}

const DIAGNOSIS_SPECIALTY_PATTERNS: DiagnosisSpecialtyPattern[] = [
  // GENERAL
  { label: "Cardiomegaly", pattern: /\bcardiomegall?y\b/gi, categories: [GENERAL] },
  { label: "Chest Pain", pattern: /\bchest pain\b/gi, categories: [GENERAL] },
  { label: "Angina", pattern: /\bangina\b/gi, categories: [GENERAL] },
  {
    label: "Coronary Artery Disease",
    pattern: /\b(?:coronary artery disease|cad)\b/gi,
    categories: [GENERAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Coronary Artery Disease (synonyms)",
    pattern: /\b(?:coronary atherosclerosis|atherosclerotic heart disease|ascvd)\b/gi,
    categories: [GENERAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  { label: "Hyperlipidemia", pattern: /\bhyperlipidemia\b/gi, categories: [GENERAL], persistsWhenHistorical: true },
  {
    label: "Hyperlipidemia (synonyms)",
    pattern: /\b(?:dyslipidemia|hypercholesterolemia|high cholesterol|hld)\b/gi,
    categories: [GENERAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Hypertension",
    pattern: /\b(?:hypertension|hypertensive(?: heart disease)?|high blood pressure)\b/gi,
    categories: [GENERAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Hypertension (abbreviation)",
    pattern: /\bhtn\b/gi,
    categories: [GENERAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  { label: "Low Blood Pressure", pattern: /\blow blood pressure\b/gi, categories: [GENERAL] },
  { label: "Hypotension", pattern: /\bhypotension\b/gi, categories: [GENERAL], pickOnly: true },
  { label: "Syncope", pattern: /\b(?:syncope|fainting)\b/gi, categories: [GENERAL] },
  {
    label: "Dizziness",
    pattern: /\b(?:dizziness|dizzy|lightheaded(?:ness)?)\b/gi,
    categories: [GENERAL],
    pickOnly: true,
  },
  { label: "Shortness of Breath", pattern: /\b(?:shortness of breath|\bsob\b)\b/gi, categories: [GENERAL] },
  { label: "Dyspnea", pattern: /\bdyspnea\b/gi, categories: [GENERAL], pickOnly: true },
  { label: "Edema", pattern: /\b(?:edema|leg swelling)\b/gi, categories: [GENERAL], pickOnly: true },
  { label: "Palpitations", pattern: /\bpalpitations?\b/gi, categories: [GENERAL] },
  {
    label: "Premature Beats",
    pattern: /\b(?:pvcs?|premature (?:ventricular|atrial) (?:contractions?|complexes?|beats?))\b/gi,
    categories: [GENERAL],
    pickOnly: true,
  },
  { label: "Murmur", pattern: /\bmurmur\b/gi, categories: [GENERAL] },
  { label: "Family History", pattern: /\bfamily history\b/gi, categories: [GENERAL] },
  { label: "Bradycardia", pattern: /\bbradycardia\b/gi, categories: [GENERAL] },
  { label: "Tachycardia", pattern: /\btachycardia\b/gi, categories: [GENERAL] },
  { label: "Nausea", pattern: /\bnausea\b/gi, categories: [GENERAL] },
  { label: "Cardiac Clearance", pattern: /\bcardiac clearance\b/gi, categories: [GENERAL] },
  {
    label: "Pre-op Clearance",
    pattern: /\b(?:surgical clearance|pre[\s-]?op(?:erative)?)\b/gi,
    categories: [GENERAL],
    pickOnly: true,
  },
  { label: "Abnormal EKG", pattern: /\babnormal ekg\b/gi, categories: [GENERAL] },
  {
    label: "Abnormal ECG or Echo",
    pattern: /\babnormal (?:ecg|electrocardiogram|echo(?:cardiogram)?)\b/gi,
    categories: [GENERAL],
    pickOnly: true,
  },

  // INTERVENTIONAL
  {
    label: "Heart Failure",
    pattern: /\bheart failure\b/gi,
    categories: [INTERVENTIONAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Heart Failure (abbreviations)",
    pattern: /\b(?:chf|hfref|hfpef|hfmref)\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Cardiomyopathy",
    pattern: /\bcardiomyopathy\b/gi,
    categories: [INTERVENTIONAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Atrial Fibrillation",
    pattern: /\b(?:a[\s.-]*fib|atrial fibrillation)\b/gi,
    categories: [INTERVENTIONAL],
    namedProviderAlsoFits: ELECTROPHYSIOLOGY,
    persistsWhenHistorical: true,
  },
  {
    label: "Atrial Fibrillation (abbreviation)",
    pattern: /\b(?:AF|PAF)\b/g,
    categories: [INTERVENTIONAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  { label: "Blood Clots", pattern: /\bblood clots?\b/gi, categories: [INTERVENTIONAL] },
  {
    label: "DVT / PE",
    pattern: /\b(?:dvt|deep vein thrombosis|pulmonary embol(?:ism|us))\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
  },
  { label: "Stroke", pattern: /\bstroke\b/gi, categories: [INTERVENTIONAL] },
  {
    label: "Transient Ischemic Attack",
    pattern: /\b(?:transient ischemic attack|tia)\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
  },
  {
    label: "Ischemic Heart Disease",
    pattern: /\bischemic heart disease\b/gi,
    categories: [INTERVENTIONAL],
    persistsWhenHistorical: true,
  },
  { label: "Heart Attack", pattern: /\bheart attack\b/gi, categories: [INTERVENTIONAL] },
  {
    label: "Myocardial Infarction",
    pattern: /\b(?<!\bold )myocardial infarction\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
  },
  {
    label: "Myocardial Infarction (abbreviation)",
    pattern: /\b(?:N?STEMI|MI)\b/g,
    categories: [INTERVENTIONAL],
    pickOnly: true,
  },
  {
    label: "Abnormal Stress Test",
    pattern: /\b(?:abnormal|positive) (?:(?:nuclear|exercise|cardiac|treadmill|lexiscan) )?stress test\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
  },
  { label: "Aneurysm", pattern: /\baneurysm\b/gi, categories: [INTERVENTIONAL], persistsWhenHistorical: true },
  {
    label: "Peripheral Artery Disease",
    pattern: /\bperipheral artery disease\b/gi,
    categories: [INTERVENTIONAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Peripheral Vascular Disease",
    pattern: /\b(?:peripheral (?:arterial|vascular) disease|claudication)\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Peripheral Vascular Disease (abbreviation)",
    pattern: /\b(?:PAD|PVD)\b/g,
    categories: [INTERVENTIONAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Carotid Stenosis",
    pattern: /\bcarotid (?:artery )?stenosis\b/gi,
    categories: [INTERVENTIONAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },

  // STRUCTURAL
  {
    label: "Aortic Stenosis",
    pattern: /\baortic(?:\s+\(?valve\)?)?\s+stenosis\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Aortic Regurgitation",
    pattern: /\baortic(?:\s+\(?valve\)?)?\s+(?:regurg(?:itation)?|insufficiency)\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Mitral Stenosis",
    pattern: /\bmitral(?:\s+\(?valve\)?)?\s+stenosis\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Mitral Regurgitation",
    pattern: /\bmitral(?:\s+\(?valve\)?)?\s+(?:regurg(?:itation)?|insufficiency)\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Valve Insufficiency",
    pattern: /\bvalve\)?\s+insufficiency\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Valve Prolapse",
    pattern: /\bvalve\)?\s+prolapse\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Valve Disease",
    pattern:
      /\b(?:(?:aortic|mitral|tricuspid|pulmonic)(?: \(?valve\)?)? (?:stenosis|regurg(?:itation)?|insufficiency|disease|disorder)|pulmonary \(?valve\)? (?:stenosis|regurg(?:itation)?|insufficiency|disease|disorder)|pulmonary (?:stenosis|regurg(?:itation)?))\b/gi,
    categories: [STRUCTURAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Valve Procedure",
    pattern:
      /\b(?:tavr|tavi|transcatheter aortic valve|mitraclip|teer|transcatheter edge[\s-]to[\s-]edge|valve (?:replacement|replaced|repair))\b/gi,
    categories: [STRUCTURAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Congenital Heart Disease",
    pattern: /\bcongenital heart disease\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "Septal Defect",
    pattern: /\b(?:pfo|patent foramen ovale|atrial septal defect)\b/gi,
    categories: [STRUCTURAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  {
    label: "Left Atrial Appendage",
    pattern: /\bleft atrial appendage\b/gi,
    categories: [STRUCTURAL],
    persistsWhenHistorical: true,
  },
  {
    label: "LAA Closure",
    pattern: /\b(?:laao|laa (?:closure|occlusion|occluder)|watchman)\b/gi,
    categories: [STRUCTURAL],
    pickOnly: true,
    persistsWhenHistorical: true,
  },
  { label: "Renal Denervation", pattern: /\brenal denervation\b/gi, categories: [STRUCTURAL] },

  // ELECTROPHYSIOLOGY
  { label: "Heart Rhythm Disorder", pattern: /\bheart rhythm disorder\b/gi, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Cardiac Arrhythmia",
    pattern: /\b(?:cardiac (?:arrhythmias?|dysrhythmias?)|arrhythmias?,? unspecified|unspecified arrhythmias?)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
  },
  { label: "Atrial Flutter", pattern: /\batrial flutter\b/gi, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Supraventricular Tachycardia",
    pattern: /\b(?:supraventricular tachycardia|svt)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
  },
  {
    label: "Ventricular Arrhythmia",
    pattern: /\b(?:ventricular (?:tachycardia|fibrillation)|v[\s-]?tach|v[\s-]?fib)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
  },
  { label: "Ventricular Arrhythmia (abbreviation)", pattern: /\b(?:NSVT|VT|VF)\b/g, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Heart Block",
    pattern:
      /\b(?:(?:second|third|2nd|3rd)[\s-]degree (?:av |atrioventricular |heart )?block|complete heart block|mobitz|(?<!(?:first|1st)[\s-]degree )(?:heart|av|atrioventricular) block(?!,? (?:first|1st)[\s-]degree))\b/gi,
    categories: [ELECTROPHYSIOLOGY],
    persistsWhenHistorical: true,
  },
  {
    label: "Sinus Node Dysfunction",
    pattern: /\b(?:sick sinus(?: syndrome)?|sinus node dysfunction|tachy[\s-]?brady(?:cardia)?(?: syndrome)?)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
    persistsWhenHistorical: true,
  },
  { label: "WPW", pattern: /\b(?:wpw|wolff[\s-]parkinson[\s-]white)\b/gi, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Long QT",
    pattern: /\blong qt\b/gi,
    categories: [ELECTROPHYSIOLOGY],
    persistsWhenHistorical: true,
  },
  { label: "Ablation", pattern: /\bablation\b/gi, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Electrophysiology Request",
    pattern: /\b(?:electrophysiology|electrophysiologist)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
  },
  {
    label: "Cardiac Device",
    pattern:
      /\b(?:pacemakers?|pacer|ppm|defibrillators?|cardiac resynchronization|crt(?:[\s-]?[dp])?|biventricular (?:pacing|pacemaker|device)|loop recorder|ilr|linq|device (?:implantation|monitoring|check|interrogation))\b/gi,
    categories: [ELECTROPHYSIOLOGY],
    persistsWhenHistorical: true,
  },
  {
    label: "ICD",
    pattern: /\ba?icd\b(?![\s-]*(?:9|10|cm\b|codes?\b|:|#))/gi,
    categories: [ELECTROPHYSIOLOGY],
    persistsWhenHistorical: true,
  },
];

const DIAGNOSIS_CODE_PREFIXES: Readonly<Record<TprSpecialtyCategory, string>> = {
  [GENERAL]:
    "I10 I11 I12 I13 I15 I16 E78 R07 R00 R01 R060 R55 R42 R60 R9431 R931 I20 I251 I517 I95 I491 I493 Z01810 Z01818 Z824",
  [INTERVENTIONAL]: "I48 I50 I42 I43 I21 I22 I255 I259 I63 G45 I26 I824 I702 I739 I71 I72 I77 I652",
  [STRUCTURAL]: "I05 I06 I07 I08 I34 I35 I36 I37 I38 I39 Q20 Q21 Q22 Q23 Q24 Q25 Q26 Z952 Z953 Z954",
  [ELECTROPHYSIOLOGY]: "Z950 Z95810 Z4501 Z4502 Z4509 T821 I441 I442 I456 I4581 I47 I490 I495 I499 I483 I484 I4892",
};

const CATEGORY_PRECEDENCE: readonly TprSpecialtyCategory[] = [ELECTROPHYSIOLOGY, STRUCTURAL, INTERVENTIONAL, GENERAL];

// A historical mention ("history of SVT", "h/o stroke") is a past event, not the condition the patient
// is being referred for, so it doesn't drive the category, unless the pattern persists (afib, valve
// disease, devices). Family history ("family hx", "FH of", "FHx:") never counts. Scoped to the
// keyword's clause like negation; a present-tense word after the marker ("hx of SVT, now with
// flutter", "history of SVT but current SVT") makes it current again. Bare "FH" isn't a marker
// because it also abbreviates familial hypercholesterolemia.
const HISTORICAL_MARKER_REGEX = /\b(?:history\s+of|hx\s+of|hx|h\/o|pmh|fhx|fh(?=\s*(?:of\b|:|-)))(?![\w/])/gi;
const FAMILY_MARKER_REGEX = /^fh/i;
const FAMILY_BEFORE_MARKER_REGEX = /\b(?:family|fam\.?)\s*$/i;
const CURRENT_AFTER_HISTORY_REGEX =
  /\b(?:now|current|currently|active|presents?|presenting|presented|new|recent|recently|acute|recurrent|recurring|ongoing|persistent|worsening|today)\b/i;

function isHistoricalMention(text: string, matchIndex: number, persistsWhenHistorical: boolean): boolean {
  const context = getClauseBeforeMatch(text, matchIndex);
  const lastMarker = Array.from(context.matchAll(HISTORICAL_MARKER_REGEX)).at(-1);

  if (lastMarker?.index === undefined) return false;
  if (FAMILY_MARKER_REGEX.test(lastMarker[0]) || FAMILY_BEFORE_MARKER_REGEX.test(context.slice(0, lastMarker.index))) {
    return true;
  }

  if (persistsWhenHistorical) return false;

  return !CURRENT_AFTER_HISTORY_REGEX.test(context.slice(lastMarker.index + lastMarker[0].length));
}

type TprDiagnosisSpecialtyMatch = {
  primary: TprSpecialtyCategory;
  namedProviderFits: TprSpecialtyCategory[];
};

type DiagnosisSpecialtyTier = {
  matched: Set<TprSpecialtyCategory>;
  namedProviderFits: Set<TprSpecialtyCategory>;
  excluded: Set<TprSpecialtyCategory>;
};

export function matchTprDiagnosisSpecialties(referral: Referral): TprDiagnosisSpecialtyMatch {
  const diagnosisTier = createDiagnosisSpecialtyTier();

  for (const diagnosis of (referral.diagnosisList ?? []) as DiagnosisItem[]) {
    const textTier = matchDiagnosisSpecialtyPatterns([diagnosis.diagnosisText]);
    const code = normalizeICD10Code(diagnosis.diagnosisCode).toUpperCase();

    textTier.matched.forEach((category) => diagnosisTier.matched.add(category));
    textTier.namedProviderFits.forEach((category) => diagnosisTier.namedProviderFits.add(category));

    for (const category of CATEGORY_PRECEDENCE) {
      const prefixes = DIAGNOSIS_CODE_PREFIXES[category].split(" ");

      if (
        code &&
        prefixes.some((prefix) => code.startsWith(prefix)) &&
        !diagnosisTextExcludesCode(diagnosis.diagnosisText, textTier, category)
      ) {
        addDiagnosisSpecialtyMatch(diagnosisTier, category, { pickOnly: category !== ELECTROPHYSIOLOGY });
      }
    }
  }

  const diagnosisListDecides = Array.from(diagnosisTier.matched).some((category) => category !== GENERAL);
  const tiers = diagnosisListDecides
    ? [diagnosisTier]
    : [diagnosisTier, matchDiagnosisSpecialtyPatterns([referral.reasonForReferral])];
  const matched = new Set(tiers.flatMap((tier) => Array.from(tier.matched)));
  const namedProviderFits = new Set(tiers.flatMap((tier) => Array.from(tier.namedProviderFits)));

  return {
    primary: CATEGORY_PRECEDENCE.find((category) => matched.has(category)) ?? GENERAL,
    namedProviderFits: CATEGORY_PRECEDENCE.filter((category) => namedProviderFits.has(category)),
  };
}

function diagnosisTextExcludesCode(
  text: string | null | undefined,
  textTier: DiagnosisSpecialtyTier,
  category: TprSpecialtyCategory
): boolean {
  if (textTier.matched.has(category)) return false;
  if (textTier.excluded.has(category)) return true;
  if (!text) return false;

  return isHistoricalMention(text, text.length, false);
}

function createDiagnosisSpecialtyTier(): DiagnosisSpecialtyTier {
  return { matched: new Set(), namedProviderFits: new Set(), excluded: new Set() };
}

function addDiagnosisSpecialtyMatch(
  tier: DiagnosisSpecialtyTier,
  category: TprSpecialtyCategory,
  { pickOnly = false, namedProviderAlsoFits }: { pickOnly?: boolean; namedProviderAlsoFits?: TprSpecialtyCategory }
) {
  tier.matched.add(category);
  if (pickOnly) return;
  tier.namedProviderFits.add(category);
  if (namedProviderAlsoFits) tier.namedProviderFits.add(namedProviderAlsoFits);
}

function matchDiagnosisSpecialtyPatterns(texts: (string | null | undefined)[]): DiagnosisSpecialtyTier {
  const tier = createDiagnosisSpecialtyTier();

  for (const { pattern, categories, persistsWhenHistorical = false, ...fit } of DIAGNOSIS_SPECIALTY_PATTERNS) {
    const isExcludedMatch = (text: string, matchIndex: number) =>
      isHistoricalMention(text, matchIndex, persistsWhenHistorical);

    if (texts.some((text) => textMatchesUnnegated(text, pattern, { isExcludedMatch }))) {
      categories.forEach((category) => addDiagnosisSpecialtyMatch(tier, category, fit));
    } else if (texts.some((text) => text && text.search(pattern) !== -1)) {
      categories.forEach((category) => tier.excluded.add(category));
    }
  }

  return tier;
}

/**
 * Physician NPI -> department IDs they practice at, transcribed from ops' provider-roster
 * reference sheet (2026-09-01). This roster deliberately supersedes the per-location NPI enums in
 * configuration.ts (`TprBiltmoreCamelbackProviderNpi` etc.) for "where does this physician
 * practice" purposes — those enums group a physician under a single location for other reasons
 * (e.g. Camelback in-scope gating) and materially undercount most physicians' actual practicing
 * locations. Do not derive location coverage from those enums; this is the source of truth for
 * that question.
 *
 * Known discrepancy: ops' sheet lists Saifuldeen Al Qaisi as West-only, while
 * `TprBiltmorePeoriaProviderNpi` groups him under Peoria. Following the sheet here; flag to ops if
 * that enum grouping should also change.
 *
 * Supervised APPs (Diana Thayer, Danielle Sturm, Kristen Jensen, Kevin Murphy, Elizabeth Steffens)
 * resolve through their supervising physician (`getTprSupervisingPhysicianNpi`). Independent
 * clinicians remain direct roster entries.
 */
export const TPR_PHYSICIAN_LOCATIONS: Readonly<Record<string, readonly string[]>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: [TPR_BILTMORE_DEPARTMENT_ID, TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: [
    TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
  ],
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: [TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: [TPR_BILTMORE_VERRADO_DEPARTMENT_ID, TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: [TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: [TPR_BILTMORE_DEPARTMENT_ID, TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: [TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: [
    TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
    TPR_BILTMORE_VERRADO_DEPARTMENT_ID,
    TPR_BILTMORE_DEPARTMENT_ID,
  ],
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: [TPR_BILTMORE_VERRADO_DEPARTMENT_ID, TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: [TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmoreWestProviderNpi.RAJ_SHAH]: [TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: [
    TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
    TPR_BILTMORE_CENTRAL_DEPARTMENT_ID,
  ],
};

/**
 * Providers TPR does not schedule through Valerie. The diagnosis-compatible pick prefers any other
 * eligible candidate and falls back to these only when they are the sole eligible providers. Kept
 * separate from `NON_VALERIE_SCHEDULING_PROVIDER_NPIS` so the post-assignment scheduling gate is
 * unchanged.
 */
export const TPR_DEPRIORITIZED_PICK_NPIS: ReadonlySet<string> = new Set<string>([
  TprBiltmoreCamelbackProviderNpi.AKIL_LOLI,
  TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU,
  TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO,
]);

/** Same ops sheet as TPR_PHYSICIAN_LOCATIONS — specialty categories per physician NPI. */
export const TPR_PHYSICIAN_SPECIALTIES: Readonly<Record<string, readonly TprSpecialtyCategory[]>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: [GENERAL],
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: [ELECTROPHYSIOLOGY],
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: [ELECTROPHYSIOLOGY],
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: [GENERAL, INTERVENTIONAL],
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreWestProviderNpi.RAJ_SHAH]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: [GENERAL],
};

/** Same ops sheet — display name (with credentials) per physician NPI, for the receiving-provider resolver's display. */
export const TPR_PHYSICIAN_DISPLAY_NAME: Readonly<Record<string, string>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: "Akil Loli, MD",
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: "Akram Ibrahim, MD",
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: "Chris Lichtenwalter, MD",
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: "Ashish Gupta, MD",
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: "Ayman Jamal, MD",
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: "David Prutzman, DO",
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: "James Kim, MD",
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: "Jason Cool, MD",
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: "Kristina Skinner, DO",
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: "Lawrence Kline, DO",
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: "Lorraine Homes, FNP-C",
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: "Maedeh Kholghi, DO",
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: "Marium Muzaffar, MD",
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: "Marwan Bahu, MD",
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: "Michelle Mix, MD",
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: "Neal Klein, MD",
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: "Nickalaus Gramze, MD",
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: "Peter Maki, MD",
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: "Jody Eckhardt, DNP",
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: "Rahool Karnik, MD",
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: "Renzo Cataldo, MD",
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: "Richard Pham, DO",
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: "Saifuldeen Al Qaisi, MD",
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: "Sharon Sadeghinia, MD",
  [TprBiltmoreWestProviderNpi.RAJ_SHAH]: "Raj Shah, MD",
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: "Timothy Byrne, DO",
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: "William Jaffe, DO",
};

/** Display name (with credentials) for a physician NPI, falling back to the supervising physician for an APP. */
export function getTprPhysicianDisplayName(providerNpi: string | null | undefined): string | null {
  if (!providerNpi) return null;

  const direct = TPR_PHYSICIAN_DISPLAY_NAME[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_DISPLAY_NAME[supervisingPhysicianNpi]) || null;
}

export function getTprAppointmentProviderDisplayName(
  appointmentProviderName: string,
  appointmentProviderNpi: string | null | undefined
): string {
  if (!appointmentProviderNpi) return appointmentProviderName;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(appointmentProviderNpi);

  if (supervisingPhysicianNpi) {
    const supervisingPhysicianName = getTprPhysicianDisplayName(supervisingPhysicianNpi);

    return supervisingPhysicianName
      ? `${appointmentProviderName} → supervising physician ${supervisingPhysicianName}`
      : appointmentProviderName;
  }

  return getTprPhysicianDisplayName(appointmentProviderNpi) ?? appointmentProviderName;
}

/**
 * Department IDs a physician practices at. Resolves an APP NPI to its supervising physician's
 * roster entry first (APPs aren't in the ops sheet — see the module doc comment). Returns an empty
 * array when the NPI isn't in the roster at all (unknown physician, or an APP with no supervising
 * physician on file).
 */
export function getTprPhysicianLocations(providerNpi: string | null | undefined): readonly string[] {
  if (!providerNpi) return [];

  const direct = TPR_PHYSICIAN_LOCATIONS[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_LOCATIONS[supervisingPhysicianNpi]) || [];
}

/**
 * Every department at least one rostered physician practices at, nearest-first callers can rank
 * against. Deliberately narrower than `getTprDepartmentIds()`: the sites missing from the ops
 * roster sheet (Terrace, Wickenburg, Kierland) are valid receiving locations but have nobody to
 * assign, so proposing one on proximity alone would only manufacture manual review.
 */
export function getTprStaffedDepartmentIds(): string[] {
  return [...new Set(Object.values(TPR_PHYSICIAN_LOCATIONS).flat())].sort((a, b) =>
    a.localeCompare(b, "en", { numeric: true })
  );
}

function getTprPhysicianSpecialties(providerNpi: string): readonly TprSpecialtyCategory[] {
  const direct = TPR_PHYSICIAN_SPECIALTIES[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_SPECIALTIES[supervisingPhysicianNpi]) || [];
}

export function doesTprPhysicianTreatSpecialties(
  providerNpi: string,
  categories: readonly TprSpecialtyCategory[]
): boolean {
  return getTprPhysicianSpecialties(providerNpi).some((category) => categories.includes(category));
}

/** True when the given physician (or their supervising physician, for an APP) practices at the department. */
export function doesTprPhysicianPracticeAtDepartment(
  providerNpi: string | null | undefined,
  departmentId: string
): boolean {
  return getTprPhysicianLocations(providerNpi).includes(departmentId);
}

/**
 * Physician NPIs who both treat at least one of the given specialty categories AND practice at the
 * given department — candidates for the receiving-provider resolver's "no receiving provider named,
 * pick one at this location who treats the diagnosis" branch. Sorted for a stable, deterministic
 * first choice rather than object-key iteration order.
 */
export function findTprPhysiciansForSpecialtiesAtDepartment(
  categories: readonly TprSpecialtyCategory[],
  departmentId: string
): string[] {
  return Object.keys(TPR_PHYSICIAN_LOCATIONS)
    .filter(
      (npi) =>
        doesTprPhysicianPracticeAtDepartment(npi, departmentId) && doesTprPhysicianTreatSpecialties(npi, categories)
    )
    .sort();
}

export type TprPhysicianOption = {
  providerNpi: string;
  displayName: string;
};

/**
 * Every rostered physician practising at a department, for the ops provider picker on the sync
 * steps. Scoped to the department on purpose: picking a provider who doesn't practise where the
 * referral is going is what produces the wrong-location care-team member, so the picker should not
 * offer the invalid pairs at all.
 *
 * Sorted by display name so the list is stable and scannable rather than in object-key order.
 */
export function listTprPhysiciansAtDepartment(departmentId: string): TprPhysicianOption[] {
  return Object.keys(TPR_PHYSICIAN_LOCATIONS)
    .filter((npi) => doesTprPhysicianPracticeAtDepartment(npi, departmentId))
    .map((npi) => ({ providerNpi: npi, displayName: getTprPhysicianDisplayName(npi) ?? npi }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
